import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { CalendarClock, AlertTriangle, Zap, PenLine } from "lucide-react";
import { useBrand } from "../context/BrandContext";

export function PublishingPolicies() {
  const brand = useBrand();
  const [settings, setSettings] = useState(null);
  const [pending, setPending] = useState(null);
  const [name, setName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/settings").then((res) => setSettings(res.data)).catch(() => {});
  }, []);

  const requestChange = (key, value, label) => {
    setName("");
    setConfirmed(false);
    setPending({ key, value, label });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/settings", {
        key: pending.key,
        value: pending.value,
        signature_name: name.trim(),
        confirmed,
      });
      setSettings(data.settings);
      setPending(null);
      toast.success("Policy updated — our team has been notified");
    } catch (err) {
      toast.error(apiError(err, "Failed to update the policy"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!settings) return null;

  return (
    <div className="mt-12 bg-white border border-black/[0.08] rounded-xl p-6" data-testid="publishing-policies-panel">
      <div className="mb-5">
        <p className="font-heading font-extrabold text-lg tracking-tight">Publishing policies</p>
        <p className="text-xs text-neutral-500 mt-1">
          Changes to these policies require your electronic signature and notify the {brand.display_name} team.
        </p>
      </div>

      <div className="flex items-start justify-between gap-4 py-4 border-t border-black/[0.06]">
        <div className="flex items-start gap-3">
          <Zap className="h-4 w-4 text-[var(--brand-primary)] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-neutral-900">Auto-approve</p>
            <p className="text-xs text-neutral-600 leading-relaxed mt-0.5 max-w-xl">
              {settings.auto_approve
                ? "ON — articles flow to publishing automatically. No approval or rejection is needed from you."
                : "OFF — every article requires your explicit Approve or Reject before anything moves forward."}
            </p>
          </div>
        </div>
        <Switch
          data-testid="auto-approve-switch"
          checked={settings.auto_approve}
          onCheckedChange={(v) => requestChange("auto_approve", v, "Auto-approve")}
        />
      </div>

      {!settings.auto_approve && (
        <div className="flex items-start justify-between gap-4 py-4 border-t border-black/[0.06]" data-testid="sixty-day-policy-row">
          <div className="flex items-start gap-3">
            <CalendarClock className="h-4 w-4 text-[var(--brand-primary)] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-neutral-900">60-day review policy</p>
              <p className="text-xs text-neutral-600 leading-relaxed mt-0.5 max-w-xl">
                Articles in queue are subject to scheduled publishing if they are not reviewed within{" "}
                <strong>60 days</strong>, so our optimization efforts operate in a timely manner. Turn this off
                and unreviewed articles will sit in queue indefinitely.
              </p>
              {!settings.sixty_day_policy && (
                <p className="text-xs font-semibold text-red-700 mt-1.5 inline-flex items-center gap-1.5" data-testid="perpetuity-warning">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Both policies are off — articles will sit in
                  queue in perpetuity, which significantly impacts the results of our optimization efforts.
                </p>
              )}
            </div>
          </div>
          <Switch
            data-testid="sixty-day-switch"
            checked={settings.sixty_day_policy}
            onCheckedChange={(v) => requestChange("sixty_day_policy", v, "60-day review policy")}
          />
        </div>
      )}

      <Dialog open={!!pending} onOpenChange={(v) => !v && setPending(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="policy-sign-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter flex items-center gap-2">
              <PenLine className="h-6 w-6 text-[var(--brand-primary)]" /> Confirm policy change
            </DialogTitle>
            <DialogDescription className="text-neutral-500 pt-1">
              You are turning <span className="font-semibold text-neutral-700">{pending?.label}</span>{" "}
              <span className="font-semibold text-neutral-700">{pending?.value ? "ON" : "OFF"}</span>.
              {pending?.key === "sixty_day_policy" && !pending?.value &&
                " Unreviewed articles will sit in queue indefinitely, which significantly impacts the results of our optimization efforts."}
              {pending?.key === "auto_approve" && pending?.value &&
                " Articles will flow to publishing automatically without individual approvals."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            <div className="space-y-2">
              <Label htmlFor="policy-signature" className="font-mono-accent text-xs uppercase tracking-[0.2em] text-neutral-500">
                Electronic Signature — Full Name
              </Label>
              <Input
                id="policy-signature"
                data-testid="policy-signature-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className="h-11 rounded-lg font-article italic text-lg"
              />
            </div>

            <div className="bg-neutral-50 border border-black/[0.06] rounded-lg px-4 py-3">
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">
                Timestamp (recorded on confirmation)
              </p>
              <p className="font-mono-accent text-sm text-neutral-700">{new Date().toLocaleString()}</p>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="policy-confirm"
                data-testid="policy-confirm-checkbox"
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                className="mt-0.5"
              />
              <label htmlFor="policy-confirm" className="text-sm text-neutral-600 leading-relaxed cursor-pointer">
                I understand the impact of this policy change and authorize it. This acts as my electronic
                signature.
              </label>
            </div>

            <Button
              data-testid="policy-sign-submit-button"
              disabled={!name.trim() || !confirmed || submitting}
              onClick={submit}
              className="w-full h-11 rounded-full bg-[var(--brand-primary)] hover:opacity-90 active:scale-95 transition-colors text-white font-semibold"
            >
              {submitting ? "Saving..." : "Sign & Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
